import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const API_BASE_URL = 'https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      console.log('Wastage API: Token obtained successfully');
      return idToken;
    }

    console.warn('Wastage API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Wastage API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `Wastage API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Wastage API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_wastage_dashboard',
        `Wastage Dashboard API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[WastageApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(
      `Wastage API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_wastage_dashboard',
      `Wastage Dashboard API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[WastageApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * Builds query string from parameters object.
 * @param {Object} params - Object with query parameters.
 * @returns {string} - Query string (without leading '?').
 */
const buildQueryString = (params) => {
  const queryParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

/**
 * API utility functions for Wastage Dashboard.
 */
export const wastageApi = {
  /**
   * Retrieves wastage dashboard data with comprehensive analytics.
   * GET /stock/wastage/dashboard
   * Lambda: innoworx-stock-wastage-dashboard
   * @param {Object} params - Query parameters for the dashboard
   * @param {string} params.site_id - Site identifier (REQUIRED)
   * @param {string} [params.period] - Time period: 'daily', 'weekly', 'monthly', 'custom' (default: 'daily')
   * @param {string} [params.date] - Specific date - YYYY-MM-DD for daily/weekly, YYYY-MM for monthly
   * @param {string} [params.start_date] - Start date (YYYY-MM-DD) - required when period='custom'
   * @param {string} [params.end_date] - End date (YYYY-MM-DD) - required when period='custom'
   * @param {string} [params.category] - Filter by category: 'prod_waste', 'serv_waste', 'exp_waste'
   * @param {number} [params.limit] - Limit for top/least lists (default: 10, max: 50)
   * @param {boolean} [params.include_unlogged] - Include unlogged wastage detection (default: true)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  getWastageDashboard: async (params) => {
    try {
      // Validate required fields
      if (!params || !params.site_id) {
        throw new Error('site_id parameter is required');
      }

      // Validate period type
      const validPeriods = ['daily', 'weekly', 'monthly', 'custom'];
      if (params.period && !validPeriods.includes(params.period)) {
        throw new Error(
          'Invalid period type. Must be: daily, weekly, monthly, or custom'
        );
      }

      // Validate custom period requires start_date and end_date
      if (params.period === 'custom') {
        if (!params.start_date || !params.end_date) {
          throw new Error(
            'start_date and end_date are required when period=custom'
          );
        }
      }

      // Build query parameters
      const queryParams = {
        site_id: params.site_id,
      };

      if (params.period) queryParams.period = params.period;
      if (params.date) queryParams.date = params.date;
      if (params.start_date) queryParams.start_date = params.start_date;
      if (params.end_date) queryParams.end_date = params.end_date;
      if (params.category) queryParams.category = params.category;
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = parseInt(params.limit, 10);
      }
      if (params.include_unlogged !== undefined && params.include_unlogged !== null) {
        queryParams.include_unlogged = params.include_unlogged.toString();
      }

      const queryString = buildQueryString(queryParams);
      const endpoint = `/stock/wastage/dashboard${queryString ? `?${queryString}` : ''}`;

      console.log(
        'Wastage API: Fetching dashboard data with params:',
        JSON.stringify(queryParams, null, 2)
      );

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Wastage API: Dashboard response received');

      return {
        success: true,
        data: responseData,
        period: responseData.period,
        summary: responseData.summary,
        categoryBreakdown: responseData.category_breakdown,
        itemWiseWastage: responseData.item_wise_wastage,
        topWastedItems: responseData.top_wasted_items,
        leastWastedItems: responseData.least_wasted_items,
        timeSeries: responseData.time_series,
        reasonBreakdown: responseData.reason_breakdown,
        unloggedWastage: responseData.unlogged_wastage,
        costAnalysis: responseData.cost_analysis,
        efficiencyMetrics: responseData.efficiency_metrics,
        comparisonMetrics: responseData.comparison_metrics,
      };
    } catch (error) {
      console.error('Wastage API: Error in getWastageDashboard:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch wastage dashboard data',
        status: error.status,
      };
    }
  },

  /**
   * Convenience method for daily wastage dashboard.
   * @param {string} siteId - Site identifier (required)
   * @param {string} [date] - Date in YYYY-MM-DD format (defaults to today)
   * @param {Object} [options] - Additional options
   * @param {string} [options.category] - Filter by category
   * @param {number} [options.limit] - Limit for top/least lists
   * @param {boolean} [options.include_unlogged] - Include unlogged wastage
   * @returns {Promise<Object>} Dashboard data
   */
  getDailyDashboard: async (siteId, date = null, options = {}) => {
    const params = {
      site_id: siteId,
      period: 'daily',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return wastageApi.getWastageDashboard(params);
  },

  /**
   * Convenience method for weekly wastage dashboard.
   * @param {string} siteId - Site identifier (required)
   * @param {string} [date] - Date in YYYY-MM-DD format (defaults to current week)
   * @param {Object} [options] - Additional options
   * @param {string} [options.category] - Filter by category
   * @param {number} [options.limit] - Limit for top/least lists
   * @param {boolean} [options.include_unlogged] - Include unlogged wastage
   * @returns {Promise<Object>} Dashboard data
   */
  getWeeklyDashboard: async (siteId, date = null, options = {}) => {
    const params = {
      site_id: siteId,
      period: 'weekly',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return wastageApi.getWastageDashboard(params);
  },

  /**
   * Convenience method for monthly wastage dashboard.
   * @param {string} siteId - Site identifier (required)
   * @param {string} [date] - Date in YYYY-MM format (defaults to current month)
   * @param {Object} [options] - Additional options
   * @param {string} [options.category] - Filter by category
   * @param {number} [options.limit] - Limit for top/least lists
   * @param {boolean} [options.include_unlogged] - Include unlogged wastage
   * @returns {Promise<Object>} Dashboard data
   */
  getMonthlyDashboard: async (siteId, date = null, options = {}) => {
    const params = {
      site_id: siteId,
      period: 'monthly',
      ...options,
    };

    if (date) {
      params.date = date;
    }

    return wastageApi.getWastageDashboard(params);
  },

  /**
   * Convenience method for custom date range wastage dashboard.
   * @param {string} siteId - Site identifier (required)
   * @param {string} startDate - Start date in YYYY-MM-DD format (required)
   * @param {string} endDate - End date in YYYY-MM-DD format (required)
   * @param {Object} [options] - Additional options
   * @param {string} [options.category] - Filter by category
   * @param {number} [options.limit] - Limit for top/least lists
   * @param {boolean} [options.include_unlogged] - Include unlogged wastage
   * @returns {Promise<Object>} Dashboard data
   */
  getCustomRangeDashboard: async (siteId, startDate, endDate, options = {}) => {
    const params = {
      site_id: siteId,
      period: 'custom',
      start_date: startDate,
      end_date: endDate,
      ...options,
    };

    return wastageApi.getWastageDashboard(params);
  },
};

